import React, { useState } from "react";
import { Tab } from "@/types";
import { cn } from "@/lib/utils";
import { useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

interface TabCardProps {
  tab: Tab;
  onClick?: () => void;
  className?: string;
  collectionId?: string;
  isOpenTab?: boolean;
}

const TabCard = ({ 
  tab, 
  onClick, 
  className,
  collectionId,
  isOpenTab = false,
}: TabCardProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: `${isOpenTab ? 'opentab' : 'collection'}-${collectionId || ''}-${tab.tab_id}`,
    data: {
      type: 'tab',
      tab: tab,
      collectionId,
      isOpenTab,
    },
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `tab-drop-${collectionId}-${tab.tab_id}`,
    data: { type: 'tab-drop', collectionId, tabId: tab.tab_id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const openTabInNewWindow = () => {
    if (import.meta.env.MODE === "development") {
      window.open(tab.url, '_blank');
    } else if (typeof chrome !== "undefined" && chrome.tabs && typeof chrome.tabs.create === "function") {
      chrome.tabs.create({ url: tab.url });
    } else {
      // Fallback for web app or unsupported browsers
      window.open(tab.url, '_blank');
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isDragging) return;

    openTabInNewWindow();
    onClick?.();
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const { onMouseDown, ...otherListeners } = listeners || {};

  return (
    <div 
      ref={node => {
        setNodeRef(node);
        setDropRef(node);
      }}
      style={style}
      {...attributes}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        onMouseDown?.(e);
      }}
      {...otherListeners}
      onDoubleClick={handleDoubleClick}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      data-dragging={isDragging}
      title="Double-click to open in new tab"
      className={cn(
        "group relative select-none",
        "flex flex-col gap-3 p-4 rounded-lg",
        "bg-muted/50 hover:bg-accent/40",
        "border border-border/10",
        "transition-all duration-200",
        "transform hover:-translate-y-0.5",
        "hover:shadow-lg hover:shadow-accent/5",
        "active:translate-y-0 active:shadow-md",
        "h-full cursor-pointer",
        isDragging && "ring-2 ring-hintword-orange/30 shadow-xl scale-[1.02] bg-accent/40",
        isOver && "ring-2 ring-hintword-orange bg-accent/30",
        className
      )}
    >
      <div className="flex items-center gap-3">
        {tab.favicon_url ? (
          <img 
            src={tab.favicon_url} 
            alt="" 
            className="w-8 h-8 rounded-lg object-contain bg-accent p-1.5" 
          />
        ) : (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent text-foreground font-medium text-sm shadow-sm">
            {tab.title.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground truncate">{tab.title}</h3>
          <p className="text-xs text-muted-foreground truncate">{tab.url}</p>
        </div>
      </div>
    </div>
  );
};

export default TabCard;
